import React, { type FC, useMemo } from 'react';
import { IS_DISK_ONLY } from 'reactApp/appHelpers/configHelpers';
import { publicInfoFileViewerToolbarEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicInfo';
import type { AttachesItem } from 'reactApp/modules/attaches/attaches.types';
import type { CloudFile } from 'reactApp/modules/storage/storage.types';
import { EditorRestrictionTooltipWrapper } from 'reactApp/ui/EditorRestriction/EditorRestrictionTooltip/EditorRestrictionTooltipWrapper';
import { CloseMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/CloseMenuButton/CloseMenuButton';
import { DownloadMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/DownloadMenuButton/DownloadMenuButton';
import { EditMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/EditMenuButton/EditMenuButton';
import { MenuInfo } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuInfo/MenuInfo';
import { MenuLogo } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuLogo/MenuLogo';
import { MenuItemName, MoreMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton';
import { PdfEditMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/PdfEditMenuButton/PdfEditMenuButton';
import { ShareMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/ShareMenuButton/ShareMenuButton';
import {
    type ViewerMenuProps,
    ViewerMenuCenter,
    ViewerMenuEnd,
    ViewerMenuStart,
    ViewerMenuWrapper,
} from 'reactApp/ui/ReactViewer/ViewerMenu/ViewerMenu';
import { Zoomer } from 'reactApp/ui/ReactViewer/ViewerMenu/Zoomer/Zoomer';

interface Props extends ViewerMenuProps {
    file: CloudFile | AttachesItem;
}

export const MainMenu: FC<Props> = ({
    file,
    showLogo,
    zoom,
    decreaseZoom,
    increaseZoom,
    isArchiveItem,
    enableFavorites,
    isAttachesEnabled,
    onPublish,
    showDownload,
    onDownload,
    showEdit,
    onEdit,
    normalizedOvidiusEnabled,
    showPdfEdit,
    onPdfEdit,
    onPdfCopyEdit,
    showImageEdit,
    onImageEdit,
    onToggleFavorites,
    onOpenParent,
    onCopy,
    allowPrinting,
    onPrint,
    onMove,
    onMail,
    onDelete,
    showClose,
    onClose,
    isEditingDisabled,
    // tempexp_18663-next-line
    onShowPublicInfo,
}) => {
    const isSingleEdit = normalizedOvidiusEnabled ? showEdit && normalizedOvidiusEnabled : showEdit;

    const isInFavorites = 'isInFavorites' in file && !!file.isInFavorites;
    // tempexp_18663-next-line
    const showPublicInfo = publicInfoFileViewerToolbarEnabled && Boolean('weblink' in file && file?.weblink);

    const moreItems = useMemo(
        () => [
            { name: MenuItemName.OPEN_IN_PARENT, onClick: onOpenParent },
            { name: MenuItemName.COPY, onClick: onCopy },
            ...(allowPrinting
                ? [
                      {
                          name: MenuItemName.PRINT,
                          onClick: () => {
                              onPrint(file);
                          },
                      },
                  ]
                : []),
            { name: MenuItemName.MOVE, divider: !enableFavorites, onClick: onMove },
            ...(enableFavorites
                ? [
                      {
                          name: MenuItemName.FAVORITE,
                          active: isInFavorites,
                          // tempexp_18663-next-line
                          divider: !showPublicInfo,
                          onClick: () => {
                              onToggleFavorites(file);
                          },
                      },
                  ]
                : []),
            // tempexp_18663-start
            ...(showPublicInfo
                ? [
                      {
                          name: MenuItemName.SHOW_PUBLIC_INFO,
                          divider: true,
                          onClick: () => {
                              onShowPublicInfo(file);
                          },
                      },
                  ]
                : []),
            // tempexp_18663-end
            ...(isAttachesEnabled && !IS_DISK_ONLY ? [{ name: MenuItemName.MAIL, divider: true, onClick: onMail }] : []),
            {
                name: MenuItemName.DELETE,
                onClick: () => {
                    onDelete(file);
                },
            },
        ],
        [
            onOpenParent,
            onCopy,
            allowPrinting,
            onPrint,
            onMove,
            enableFavorites,
            isInFavorites,
            onToggleFavorites,
            isAttachesEnabled,
            onMail,
            onDelete,
            // tempexp_18663-start
            showPublicInfo,
            onShowPublicInfo,
            file.id,
            // tempexp_18663-end
        ]
    );

    return (
        <ViewerMenuWrapper>
            <ViewerMenuStart>
                {showLogo && <MenuLogo />}
                <MenuInfo file={file} />
            </ViewerMenuStart>
            <ViewerMenuCenter>{zoom && <Zoomer value={zoom} decrease={decreaseZoom} increase={increaseZoom} />}</ViewerMenuCenter>
            <ViewerMenuEnd>
                {isArchiveItem ? (
                    <>
                        {showDownload && (
                            <DownloadMenuButton
                                file={file}
                                onClick={() => {
                                    onDownload(file);
                                }}
                            />
                        )}
                    </>
                ) : (
                    <>
                        <ShareMenuButton
                            onClick={() => {
                                onPublish(file);
                            }}
                        />
                        {showDownload && (
                            <DownloadMenuButton
                                file={file}
                                onClick={() => {
                                    onDownload(file);
                                }}
                            />
                        )}
                        {isSingleEdit ? (
                            <EditorRestrictionTooltipWrapper isEditingDisabled={isEditingDisabled}>
                                <EditMenuButton
                                    onClick={() => {
                                        if (isEditingDisabled) {
                                            return;
                                        }
                                        onEdit(file);
                                    }}
                                />
                            </EditorRestrictionTooltipWrapper>
                        ) : (
                            <>
                                {showPdfEdit && (
                                    <PdfEditMenuButton
                                        onPdfEdit={() => {
                                            onPdfEdit(file);
                                        }}
                                        onPdfCopyEdit={() => {
                                            onPdfCopyEdit(file);
                                        }}
                                    />
                                )}
                            </>
                        )}
                        {showImageEdit && (
                            <EditMenuButton
                                onClick={() => {
                                    onImageEdit(file);
                                }}
                            />
                        )}
                        <MoreMenuButton list={moreItems} />
                    </>
                )}
                {showClose && (
                    <CloseMenuButton
                        onClick={() => {
                            onClose(file);
                        }}
                    />
                )}
            </ViewerMenuEnd>
        </ViewerMenuWrapper>
    );
};
